import { EditIcon, FlagIcon } from "@xola/icons";
import { ComboBox, Input } from "@xola/ui-kit";
import {
    FormField,
    isAutoGeneratedName,
    isExperienceProduct,
    isPackageProduct,
    SecondaryButton,
    SwitchField,
} from "common";
import { useField, useFormikContext } from "formik";
import { sortBy } from "lodash";
import React, { ChangeEvent, MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { components, DropdownIndicatorProps } from "react-select";
import { Form, Guest, Product, Seller, UserDetailsRequirement } from "types";
import { useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";
import { useSeller } from "../../hooks/useAccount";
import { getCustomerNamePreference, hasUniqueGuestIdentification } from "../../models/Experience";
import {
    applySharedAddonOverrides,
    getGuestPlaceholderName,
    updateGuestsWithPlaceholderNames,
} from "../../models/Guest";
import { getProductIdsInPackage, isSharedDemographicsPackage } from "../../models/Package";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { QuestionnaireModal } from "../Questionnaire/QuestionnaireModal";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { handleGuestListChange, handlePackageGuestListChange } from "./helpers/PurchaseCreateHelpers";
import { useGiftOnlyOrganizerRequirement } from "./hooks/useGiftDetailsRequirements";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { useVisibility } from "@/hooks/useVisibility";
import { canSearchCustomers } from "@/stores/globalStore";

interface CreatePurchaseCustomerNameProps {
    product: Product;
    isPurchaserAttending: boolean;
    onShow: () => void;
    onRemove: () => void;
    onEditContactClick: () => void;
    setInitialCustomerName?: (customerName: string) => void;
}

export const CreatePurchaseCustomerName = ({
    product,
    isPurchaserAttending,
    onShow,
    onRemove,
    onEditContactClick,
    setInitialCustomerName,
}: CreatePurchaseCustomerNameProps) => {
    const seller = useSeller();
    const [field, , helpers] = useField("customerName");

    const [nameRequired, customerName, isSameAsRecipient] = useCustomerDetailsStore(
        (state) => [state.nameRequired, state.customerName, state.isSameAsRecipient],
        shallow,
    );

    const isSharedPackage = useCreatePurchaseStore(
        (state) => !!state.getPurchasePackageId() && !state.isVariableDemographicsPackage(),
    );
    const [organizer, isMist] = useCreatePurchaseStore((state) => [state.organizer, state.isMist()], shallow);
    const demographicLineItems = useCreatePurchaseStore((state) => state.getDemographicLineItems());

    const guestsFromStore = useCreatePurchaseStore((state) => state.getActiveItem().guests);

    const guests = useMemo(() => guestsFromStore ?? [], [guestsFromStore]);

    const hasMultipleGuests = (guests ?? []).length > 1;

    const form = useCreatePurchaseStore((state) => state.getForm());

    const disableRequiredFormFields = useCreatePurchaseStore(
        (state) => state.getActiveItem().disableRequiredFormFields,
    );

    const isPackage = isPackageProduct(product);

    const [menuIsOpen, { show: showMenu, hide: hideMenu }] = useVisibility(false);
    const [isQuestionnaireModalVisible, setIsQuestionnaireModalVisible] = useState<boolean>(false);
    const [guestToBeEdited, setGuestToBeEdited] = useState<Guest | null>(null);

    const [localName, setLocalName] = useState(isAutoGeneratedName(field.value) ? "" : field.value);

    const isAttendeeUnique = isExperienceProduct(product) ? hasUniqueGuestIdentification(product) : true;
    const onChangeGuestsCallback = isSharedPackage ? handlePackageGuestListChange : handleGuestListChange;

    const namePreference = getCustomerNamePreference(product, seller.preferences.gift);
    const canSearchTravelers = canSearchCustomers();
    const [showToggle, setShowToggle] = useState(namePreference === UserDetailsRequirement.OPTIONAL_CONFIRMATION);

    const sortedGuests = useMemo(() => {
        return sortBy(updateGuestsWithPlaceholderNames(guests ?? [], demographicLineItems), [
            (guest: Guest) => !guest.primary,
        ]);
    }, [guests, demographicLineItems]);

    const selectInputElement: HTMLInputElement = document.querySelector("#select-input")!;

    const hasOrganizerOnlyResponses = useMemo(() => form?.fields.some((field) => field.organizerOnly), [form]);

    const updateGuestName = useCallback(
        (name: string) => {
            if (!guests?.length) return;

            const updatedGuests = guests.map((guest, index) => (index === 0 ? { ...guest, name } : guest));

            onChangeGuestsCallback(updatedGuests);
        },
        [guests, onChangeGuestsCallback],
    );

    const shouldUpdateGuestName =
        localName &&
        !isAutoGeneratedName(localName) &&
        guests.length > 0 &&
        !isAttendeeUnique &&
        localName !== guests[0].name;

    // It's used to fill in guest[0] name in the same time if Experience uses not unique attendees config from Guest Management
    // we will update the name only if the name is not autogenerated, else initially if 1 adults and 0 children is there, the name will be
    // Adults #!, incase we remove adults and add children, the guest name will have children #1, but this will try to update with adults #1
    // leading for infinite loop of updates.
    useEffect(() => {
        if (shouldUpdateGuestName) {
            updateGuestName(localName);
        }
    }, [shouldUpdateGuestName, localName, updateGuestName]);

    useEffect(() => {
        if (isPackage) {
            const experiences = useCreatePurchaseStore.getState().getProductsInPurchase();
            const experienceIds = getProductIdsInPackage(product);
            const packageExperiences = experiences.filter((experience) => experienceIds?.includes(experience.id));
            const isAllExperienceCustomerNameOptional = packageExperiences.every(
                (experience) => experience.customerNamePreference === UserDetailsRequirement.OPTIONAL_CONFIRMATION,
            );
            setShowToggle(isAllExperienceCustomerNameOptional);
            if (isAllExperienceCustomerNameOptional) {
                useCustomerDetailsStore.setState({ nameRequired: true });
            }
        } else if (isMist && nameRequired) {
            setShowToggle(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPackage, isMist]);

    useEffect(() => {
        if (setInitialCustomerName) {
            setInitialCustomerName(field.value);
        }
    }, [field, setInitialCustomerName]);

    const isDisabled = !!organizer;
    // In gift checkout a switched-off name means the seller confirmed it is not collected.
    const isNameNotNeeded = useGiftOnlyOrganizerRequirement() !== null && showToggle && !nameRequired;
    const isInputLocked = isDisabled || isSameAsRecipient;
    const isDropdownSelection =
        (!isMist || isSharedDemographicsPackage(product)) && isPurchaserAttending && hasMultipleGuests;

    const closeQuestionnaireModal = () => {
        setIsQuestionnaireModalVisible(false);
        setGuestToBeEdited(null);
    };

    const handleChangePurchaser = useDebouncedCallback((purchaser: Guest) => {
        const guests = useCreatePurchaseStore.getState().getActiveItem().guests ?? [];
        const reassignedGuests = guests.map((guest) => {
            const isOrganizer = guest.id === purchaser.id;

            return {
                ...(isOrganizer ? purchaser : guest),
                primary: purchaser.id ? isOrganizer : guest.primary,
                organizer: isOrganizer,
            };
        });
        const newGuests = applySharedAddonOverrides(reassignedGuests, purchaser);

        const { activeItemIndex } = useCreatePurchaseStore.getState();
        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            if (hasOrganizerOnlyResponses) {
                draft.items[activeItemIndex].formResponse = purchaser.formResponse;
            }

            draft.items[activeItemIndex].guests = newGuests;
        });
    }, 400);

    const handleQuestionnaireSubmit = async (updatedGuest: Guest) => {
        handleChangePurchaser(updatedGuest);

        closeQuestionnaireModal();
    };

    const handleGuestEdit = (guest: Guest) => {
        setGuestToBeEdited(guest);
        setIsQuestionnaireModalVisible(true);
    };

    const handleChangePurchaserValue = (guest: Guest) => {
        setLocalName(guest.name);
        handleChangePurchaser(guest);
        hideMenu();

        setTimeout(() => {
            selectInputElement?.focus();
        }, 0);
    };

    const handleInputChangeForComboBox = useCallback((name: string, action: any) => {
        if (action?.action === "input-change" || !action) {
            setLocalName(name);
        }
    }, []);

    const handleRemove = () => {
        onRemove();
        setLocalName("");
    };

    const handleNameChange = useDebouncedCallback((event: ChangeEvent<HTMLInputElement>) => {
        if (isPurchaserAttending && event.target.value === "" && guests.length > 0) {
            /** When user clears out the customer name field, we need to set the `field.value` to auto-generated guest name rather than setting to "". (only when guests data is available i.e. user is doing experience checkout) */
            event.target.value = getGuestPlaceholderName(guests[0], 1, demographicLineItems);
        }

        helpers.setValue(localName);
    }, 1000);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setLocalName(e.target.value);
    };

    useEffect(() => {
        setLocalName(customerName);
    }, [customerName]);

    const updateNameToStore = (localName: string) => {
        useCustomerDetailsStore.setState((state) => {
            if (state.customerName !== localName) {
                state.customerName = localName;
            }
        });
    };

    const handleSelectBlur = () => {
        hideMenu();
        helpers.setValue(localName);
        updateNameToStore(localName);
    };

    const handleInputBlur = () => {
        handleNameChange.flush();
        helpers.setValue(localName);
        updateNameToStore(localName);
    };

    return (
        <>
            <FormField
                name="customerName"
                label={
                    <CustomerNameLabel showToggle={showToggle} isChecked={!!nameRequired} isOrganizer={isInputLocked} />
                }
            >
                <div className="flex space-x-2">
                    {isDropdownSelection ? (
                        <ComboBox
                            isCreatable
                            blurInputOnSelect
                            inputId="select-input"
                            isRequired={nameRequired}
                            className="w-full text-gray-darker"
                            options={sortedGuests}
                            menuIsOpen={menuIsOpen}
                            disabled={isInputLocked || isNameNotNeeded}
                            getOptionValue={(option: any) => option.id}
                            getOptionLabel={(option: any) => option.name}
                            components={{
                                Option: (props: OptionProps) => {
                                    if (!props.value) {
                                        return null;
                                    }

                                    return (
                                        <Option
                                            {...props}
                                            organizerOnly={form?.organizerOnly ?? false}
                                            onEdit={handleGuestEdit}
                                        />
                                    );
                                },
                                DropdownIndicator: (props: DropdownIndicatorLocalProps) => (
                                    <DropdownIndicator
                                        {...props}
                                        menuIsOpen={menuIsOpen}
                                        showMenu={showMenu}
                                        hideMenu={hideMenu}
                                    />
                                ),
                            }}
                            placeholder={
                                <NoTranslate as="div" className="text-gray-darker">
                                    {field.value}
                                </NoTranslate>
                            }
                            inputValue={isAutoGeneratedName(field.value) ? undefined : localName}
                            filterOption={() => true}
                            value={localName || "Name"}
                            onFocus={(e: any) => e.preventDefault()}
                            onClick={(e: any) => e.preventDefault()}
                            onBlur={handleSelectBlur}
                            onInputChange={handleInputChangeForComboBox}
                            onChange={handleChangePurchaserValue}
                        />
                    ) : (
                        <Input
                            {...field}
                            isRequired={!!nameRequired}
                            disabled={isInputLocked || isNameNotNeeded}
                            className="no-translate"
                            placeholder={field.value || "Name"}
                            value={isAutoGeneratedName(field.value) ? undefined : localName}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                        />
                    )}
                    {canSearchTravelers &&
                        (isDisabled ? (
                            <>
                                <SecondaryButton
                                    data-testid="contact-information-edit-contact-info-button"
                                    icon={<EditIcon />}
                                    onClick={onEditContactClick}
                                >
                                    Edit
                                </SecondaryButton>
                                <SecondaryButton className="shrink-0" onClick={handleRemove}>
                                    Remove Customer
                                </SecondaryButton>
                            </>
                        ) : (
                            <SecondaryButton className="shrink-0" onClick={onShow}>
                                Search Customers
                            </SecondaryButton>
                        ))}
                </div>
            </FormField>

            {isDropdownSelection && guestToBeEdited ? (
                <QuestionnaireModal
                    isOpen={isQuestionnaireModalVisible}
                    form={form ?? getEmptyForm(seller, product)}
                    guest={guestToBeEdited}
                    disableRequiredFields={disableRequiredFormFields ?? false}
                    onClose={closeQuestionnaireModal}
                    onSubmit={handleQuestionnaireSubmit}
                />
            ) : null}
        </>
    );
};

interface DropdownIndicatorLocalProps {
    showMenu: () => void;
    hideMenu: () => void;
    menuIsOpen: boolean;
}

const DropdownIndicator = ({ showMenu, hideMenu, menuIsOpen, ...rest }: DropdownIndicatorLocalProps) => {
    const handleMouseDown = (e: any) => {
        e.stopPropagation();
        e.preventDefault();

        if (menuIsOpen) {
            hideMenu();
        } else {
            showMenu();
        }
    };

    return (
        <div onMouseDown={handleMouseDown}>
            <components.DropdownIndicator {...(rest as DropdownIndicatorProps)} />
        </div>
    );
};

interface OptionProps {
    data: Guest;
    innerRef: any;
    innerProps: any;

    /** Is the form organizer only? if yes, edit icon would be shown only for primary guest */
    organizerOnly?: boolean;
    value?: string;

    /** Callback fired when user clicks on 'Edit Purchase Name' button */
    onEdit?: (guest: Guest) => void;
}

const Option = ({ data, organizerOnly, innerRef, innerProps, onEdit }: OptionProps) => {
    const editable = organizerOnly ? organizerOnly && data.primary : true;

    const handleEdit: MouseEventHandler = (event) => {
        event.stopPropagation();
        onEdit?.(data);
    };

    return (
        <div
            ref={innerRef}
            className="cursor-pointer space-x-2.5 border-t border-gray-light px-4 py-3.5 align-text-top font-bold leading-4 hover:bg-gray-hover"
            {...innerProps}
        >
            <div className="flex w-full items-center justify-between space-x-1">
                {data.primary && <FlagIcon className="mr-1" />}

                <NoTranslate as="div" className="min-w-20 flex-grow">
                    {data.name}
                </NoTranslate>

                {editable && (
                    <SecondaryButton size="tiny" onClick={handleEdit}>
                        <EditIcon className="ml-1" />
                    </SecondaryButton>
                )}
            </div>
        </div>
    );
};

interface CustomerNameLabelProps {
    showToggle: boolean;
    isChecked: boolean;
    isOrganizer: boolean;
}

const CustomerNameLabel = ({ showToggle, isChecked, isOrganizer }: CustomerNameLabelProps) => {
    const { setFieldValue, setFieldError } = useFormikContext<CheckoutFormValues>();

    const handleChange = (nameRequired: boolean) => {
        useCustomerDetailsStore.setState({ nameRequired });

        if (!nameRequired) {
            setFieldValue("customerName", "");
            setFieldError("customerName", "");
        }
    };

    if (showToggle && !isOrganizer) {
        return (
            <SwitchField size="small" font="font-bold" direction="left" isChecked={isChecked} onChange={handleChange}>
                Name
            </SwitchField>
        );
    }

    return <>Name</>;
};

/** When no questionnaire/form is added for a product, we'll use this to show questionnaire modal (So, user can edit the guest "name" from there) */
const getEmptyForm = (seller: Seller, product: Product): Form => ({
    organizerOnly: false,
    seller: {
        id: seller.id,
    },
    product: {
        id: product.id,
    },
    productType: product.object,
    fields: [],
});
